import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Sheet – a slide-in drawer from the right side.
 */

const Sheet = ({ open, onOpenChange, children }) => {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onOpenChange]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="sheet-overlay"
        className={cn(
          'sheet-overlay',
          open ? 'sheet-overlay-visible' : 'sheet-overlay-hidden'
        )}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      {/* Drawer panel */}
      <div
        data-testid="sheet-panel"
        className={cn(
          'sheet-panel',
          open ? 'sheet-panel-visible' : 'sheet-panel-hidden'
        )}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </>
  );
};

const SheetContent = React.forwardRef(({ className, children, onClose, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('sheet-content', className)}
    {...props}
  >
    {/* Close button */}
    {onClose && (
      <button
        onClick={onClose}
        className="sheet-close-button"
        aria-label="Close"
      >
        <X className="sheet-close-icon" />
        <span className="sr-only">Close</span>
      </button>
    )}
    {children}
  </div>
));
SheetContent.displayName = 'SheetContent';

export { Sheet, SheetContent };
